<template>
  <main class="container page-wrapper">
    <div
      class="affiliate"
      :class="{ 'is-invalid': !affiliate.isValid }"
      v-if="affiliate.showMessage && affiliate.message"
      v-html="affiliate.message"
      data-testid="affiliate-message"
    />

    <div class="intro">
      <h1 class="page-title" data-testid="page-title">
        Tell Us About Your House
      </h1>
      <h2>
        We need this to give you an accurate price for your bills
      </h2>
    </div>

    <BackButton class="back-link" />

    <form
      class="form"
      novalidate
      autocomplete="on"
      @submit.prevent="formSubmit"
    >
      <WarningPanel
        v-if="errorResponse"
        :title="'There\'s a problem...'"
        :msg="errorResponse"
        :severe="true"
        class="mt-0"
      />

      <fieldset class="form__fieldset" v-if="!isManualAddress">
        <div
          class="form-item"
          :class="{ '--has-error': $v.selectedAutocompletedAddress.$error }"
        >
          <label class="form__label" for="addressFinder">
            <span class="form__label-text flex-column">
              <span class="d-flex">
                <span class="label">Your address</span>
                <a
                  @click.stop="isManualAddress = true"
                  href="#"
                  class="help"
                  data-testid="manual-address"
                  >Enter address manually</a
                >
              </span>
              <FormError
                v-if="
                  $v.selectedAutocompletedAddress.$error &&
                    !$v.selectedAutocompletedAddress.$required
                "
                data-testid="address-error"
                msg="Address required"
              />
            </span>
          </label>
          <address-autocomplete
            id="addressFinder"
            v-model="selectedAutocompletedAddress"
            @addressSelected="onAutocompleteAddressSelected"
          />
        </div>
      </fieldset>

      <fieldset class="form__fieldset" v-else>
        <div
          class="form-item"
          :class="{ '--has-error': $v.fullAddress.line1.$error }"
        >
          <label class="form__label" for="addressLine1">
            <span class="form__label-text flex-column">
              <span class="d-flex">
                <span class="label">Your address line 1</span>
                <a
                  @click.stop="isManualAddress = false"
                  href="#"
                  class="help"
                  data-testid="quick-address"
                  >Pick address from list</a
                >
              </span>
              <FormError
                v-if="
                  $v.fullAddress.line1.$error && !$v.fullAddress.line1.$required
                "
                data-testid="line-1-error"
                msg="Line 1 required"
              />
            </span>
          </label>
          <input
            class="form__input"
            type="text"
            id="addressLine1"
            data-testid="addressLine1"
            name="addressLine1"
            required
            autocomplete="address-line1"
            v-model.trim="fullAddress.line1"
          />
        </div>

        <div class="form-item">
          <label class="form__label" for="addressLine2">
            <span class="form__label-text">
              Address line 2 (optional)
            </span>
          </label>
          <input
            class="form__input"
            type="text"
            id="addressLine2"
            data-testid="addressLine2"
            name="addressLine2"
            autocomplete="address-line2"
            v-model.trim="fullAddress.line2"
          />
        </div>

        <div
          class="form-item"
          :class="{ '--has-error': $v.fullAddress.city.$error }"
        >
          <label class="form__label" for="addressCity">
            <span class="form__label-text">City</span>
            <FormError
              v-if="
                $v.fullAddress.city.$error && !$v.fullAddress.city.$required
              "
              data-testid="city-error"
              msg="City required"
            />
          </label>
          <input
            class="form__input"
            type="text"
            id="addressCity"
            data-testid="addressCity"
            name="addressCity"
            required
            autocomplete="address-level2"
            v-model.trim="fullAddress.city"
          />
        </div>

        <div
          class="form-item"
          :class="{ '--has-error': $v.fullAddress.postcode.$error }"
        >
          <label class="form__label" for="addressPostcode">
            <span class="form__label-text">Postcode</span>
            <FormError
              v-if="
                $v.fullAddress.postcode.$error &&
                  !$v.fullAddress.postcode.$required
              "
              data-testid="postcode-error"
              msg="Postcode required"
            />
          </label>
          <input
            class="form__input"
            type="text"
            id="addressPostcode"
            data-testid="addressPostcode"
            name="addressPostcode"
            required
            autocomplete="postal-code"
            v-model.trim="fullAddress.postcode"
          />
        </div>
      </fieldset>

      <InfoBox class="infobox">
        <template v-slot>
          <p>
            Can't find your address? Call our helpful agents for a quote on
            <b><a href="tel:03300539350">0330 053 9350</a></b> (9am-5pm
            Monday-Friday).
          </p>
        </template>
      </InfoBox>

      <fieldset class="form__fieldset">
        <div
          class="form-item"
          :class="{ '--has-error': $v.housemateCount.$error }"
        >
          <label class="form__label" for="housemateCount">
            <span class="form__label-text">
              <span class="label">How many people will live here?</span>
              <a @click.stop="toggleHousematesInfoPanel" href="#" class="help"
                >Why?</a
              ></span
            >
            <FormError
              v-if="$v.housemateCount.$error"
              :msg="
                `Must be a number between ${minHousemateCount} and ${maxHousemateCount}`
              "
              data-testid="housemate-count-error"
            />
          </label>

          <div class="housemate-picker">
            <button
              type="button"
              class="housemate-picker__btn housemate-picker__btn--minus"
              data-testid="decreaseHousemateCount"
              @click="decreaseHousemateCount"
              :disabled="housemateCount <= minHousemateCount"
            >
              <span class="housemate-picker__btn-text">Reduce</span>
            </button>
            <div class="field-separator" />
            <input
              type="number"
              class="form__input housemate-picker__input"
              id="housemateCount"
              data-testid="housemateCount"
              :min="minHousemateCount"
              :max="maxHousemateCount"
              required
              v-model.number="housemateCount"
            />
            <div class="field-separator" />
            <button
              type="button"
              class="housemate-picker__btn housemate-picker__btn--plus"
              data-testid="increaseHousemateCount"
              @click="increaseHousemateCount"
              :disabled="housemateCount >= maxHousemateCount"
            >
              <span class="housemate-picker__btn-text">Increase</span>
            </button>
          </div>
        </div>
      </fieldset>

      <fieldset class="form__fieldset">
        <div class="form-item">
          <label class="form__label" for="letting-agent-name">
            <span class="form__label-text"
              >Who is your letting agent? (Optional)</span
            >
          </label>
          <input
            class="form__input"
            type="text"
            id="letting-agent-name"
            data-testid="letting-agent-name"
            name="letting-agent-name"
            required
            v-model.trim="lettingAgentName"
            @input="lettingAgentNameError = ''"
          />
          <FormError
            v-if="lettingAgentNameError"
            :msg="lettingAgentNameError"
            data-testid="letting-agent-name-error"
          />
        </div>
      </fieldset>

      <div class="btn-flex-wrapper">
        <button
          class="btn btn--big form__submit btn--get-price"
          data-testid="submit-household"
          type="submit"
          @click="trackSubmitBtn"
        >
          Next
        </button>
      </div>

      <ProductInfoOverlay
        v-if="housemateInfoPanelShowing"
        title="How many people will live here?"
        info="Let us know how many people will be living at the house, including yourself. Your price will be based on how many people will be splitting the bills."
        @close-info-panel="toggleHousematesInfoPanel"
      />
    </form>
  </main>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import { between, required, requiredIf } from "vuelidate/lib/validators";
import WarningPanel from "@/components/WarningPanel";
import FormError from "@/components/forms/FormError";
import ProductInfoOverlay from "@/components/ProductInfoOverlay";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { findDiscountCode } from "@/utility/discount";
import InfoBox from "@/components/InfoBox.vue";

export default {
  name: "GetQuote",
  data() {
    return {
      errorResponse: "",
      lettingAgentNameError: "",
      housemateInfoPanelShowing: false,
      isManualAddress: false,
      fullAddress: {
        line1: "",
        line2: "",
        city: "",
        postcode: "",
      },
    };
  },
  computed: {
    ...mapState([
      "affiliate",
      "agent",
      "houseInformation",
      "leadId",
      "minHousemateCount",
      "maxHousemateCount",
    ]),
    lettingAgentName: {
      get() {
        return this.$store.state.houseInformation.lettingAgentName;
      },
      set(value) {
        this.$store.commit("SET_AGENT_NAME", value);
      },
    },
    housemateCount: {
      get() {
        return this.$store.state.housemateCount;
      },
      set(value) {
        this.$store.commit("SET_HOUSEMATE_COUNT", value);
      },
    },
    isViaShareLink: {
      get() {
        return this.$store.state.uiState.isViaShareLink;
      },
    },
    selectedAutocompletedAddress: {
      get() {
        return this.$store.state.selectedAutocompletedAddress;
      },
      set(value) {
        this.$store.commit("SET_AUTOCOMPLETED_ADDRESS", value);
      },
    },
  },
  methods: {
    ...mapActions(["getAffiliateMessage", "getProducts", "getUIConfiguration"]),
    ...mapMutations({
      setProducts: "SET_PRODUCTS",
      showLoadingPanel: "SHOW_LOADING_PANEL",
      hideLoadingPanel: "HIDE_LOADING_PANEL",
      setIsViaShareLink: "IS_VIA_SHARELINK",
      setProductOption: "SET_PRODUCT_OPTION",
      setPickedProductPrice: "SET_PICKED_PRODUCT_PRICE",
      showBackButton: "SHOW_BACK_BTN",
      hideBackButton: "HIDE_BACK_BTN",
      setDiscountState: "SET_DISCOUNT_STATE",
      setLeadTenantAddress: "SET_LEAD_TENANT_ADDRESS",
      setPostcode: "SET_POSTCODE",
    }),
    toggleHousematesInfoPanel() {
      this.housemateInfoPanelShowing = !this.housemateInfoPanelShowing;
    },
    decreaseHousemateCount() {
      if (this.housemateCount > this.minHousemateCount) {
        this.$store.commit("DECREMENT_HOUSEMATE_COUNT");
      }
    },
    increaseHousemateCount() {
      if (this.housemateCount < this.maxHousemateCount) {
        this.$store.commit("INCREMENT_HOUSEMATE_COUNT");
      }
    },
    onAutocompleteAddressSelected(address) {
      this.setLeadTenantAddress(address === null ? {} : address);
    },
    formSubmit() {
      this.$v.$touch();
      this.lettingAgentNameError = "";

      if (!this.$v.$invalid) {
        this.showLoadingPanel();
        this.errorResponse = "";

        const forceRefreshAddress = !!this.$route.query.fra;

        let postcode = this.isManualAddress
          ? this.fullAddress.postcode
          : this.houseInformation.address.postcode;
        postcode = postcode.toUpperCase();

        this.setPostcode(postcode);

        if (this.isManualAddress) {
          this.setLeadTenantAddress({
            fullAddress: {
              line1: this.fullAddress.line1,
              line2: this.fullAddress.line2,
              city: this.fullAddress.city,
            },
          });
        }

        const addressGuid = this.isManualAddress
          ? null
          : this.houseInformation.address.guid;

        const discountCode = findDiscountCode(this.$route.query, this.$store);

        this.getProducts({
          addressGuid,
          postcode: postcode,
          housemateCount: this.housemateCount,
          discountCode,
          forceRefreshAddress,
          lettingAgentName: this.lettingAgentName,
        })
          .then(({ data }) => {
            if (data.existingAccountGUID) {
              this.setLeadTenantAddress(data.address);
              this.setPostcode(data.address.postcode);

              this.$router.push({
                name: "account-already-exists",
                params: { guid: data.existingAccountGUID },
              });
            } else {
              this.setProducts(data.products);

              if (data.discount !== undefined) {
                this.setDiscountState({
                  ...data.discount,
                  isValid: true,
                });
              } else {
                this.setDiscountState({ isValid: false });
              }

              if (this.agent.isValid) {
                // Select all default products
                for (let i = 0; i < data.products.length; i++) {
                  const product = data.products[i];
                  const selectedOption = product.options.find(
                    (op) => product.selected === op.code,
                  );

                  this.setProductOption({
                    type: product.type,
                    code: selectedOption.code,
                  });

                  this.setPickedProductPrice({
                    index: i,
                    price: selectedOption.price.current,
                  });
                }

                this.$router.push(`/short-summary`);
              } else {
                this.$router.push(`/products/${data.products[0].type}`);
              }
            }
          })
          .catch((error) => {
            if (!error.response) {
              this.errorResponse =
                "Oh no! There was a problem with your request, please try again or call us 0330 053 9351";
            } else if (
              error.response.data &&
              error.response.data.errors &&
              error.response.data.errors.lettingAgentName &&
              error.response.data.errors.lettingAgentName.length
            ) {
              this.lettingAgentNameError =
                error.response.data.errors.lettingAgentName[0];
              this.errorResponse =
                error.response.data.message ||
                "There was a problem with your request.";
            } else {
              this.errorResponse = error.response.data.messages
                ? error.response.data.messages[0]
                : error.response.data.message ||
                  "There was a problem with your request.";
            }
          })
          .finally(() => {
            this.hideLoadingPanel();
          });
      }
    },
    trackSubmitBtn() {
      this.trackGoogleEvent(
        "Click Get a price button",
        "Get Price",
        "Begin build package",
      );

      this.trackFacebookEvent("Click Get a price button");
    },
  },
  validations() {
    return {
      selectedAutocompletedAddress: {
        required: requiredIf(() => !this.isManualAddress),
      },
      fullAddress: {
        line1: {
          required: requiredIf(() => this.isManualAddress),
        },
        city: {
          required: requiredIf(() => this.isManualAddress),
        },
        postcode: {
          required: requiredIf(() => this.isManualAddress),
        },
      },
      housemateCount: {
        required,
        between: between(this.minHousemateCount, this.maxHousemateCount),
      },
    };
  },
  created() {
    let affiliateCode = this.$route.query.aff || localStorage.affiliateCode;
    if (!this.leadId && affiliateCode) {
      this.getAffiliateMessage(affiliateCode);
    }

    this.showLoadingPanel();

    this.getUIConfiguration().then(() => {
      this.hideLoadingPanel();
    });

    // Tracking page hit events
    this.trackGoogleEvent(
      "Land on Household Details Page",
      "Get Price",
      "House details",
    );
    this.trackFacebookEvent("Land on Household Details Page");

    // reset viaSharelink status as we're back on the first step now
    if (this.isViaShareLink) {
      this.setIsViaShareLink(false);
      this.hideBackButton();
    } else {
      this.showBackButton();
    }
  },
  components: {
    InfoBox,
    AddressAutocomplete,
    FormError,
    ProductInfoOverlay,
    WarningPanel,
  },
};
</script>

<style lang="scss" scoped>
.intro {
  h2 {
    margin: 0 0 3rem 0;
    font-size: 18px;
  }
}

.form__label-text {
  display: flex;

  .label {
    flex-grow: 1;
  }

  .help {
    color: $brand-green;
    align-self: flex-end;
  }
}

.btn-flex-wrapper {
  display: flex;
  justify-content: center;
}

.btn--get-price {
  @include media("<medium") {
    margin-top: 0 !important;
  }
}

#addressPostcode {
  text-transform: uppercase;
}

.housemate-picker {
  display: flex;
  align-items: center;
  background-color: $white;
  border: 2px solid $grey-mid;
  border-radius: $border-radius-small;

  .form-item.--has-error & {
    border-color: $error;
  }

  input:focus {
    outline: none;
  }

  .field-separator {
    border-left: 2px solid $grey-mid;
    border-radius: 1px;
    width: 2px;
    height: 4rem;
  }
}

.housemate-picker__btn {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  flex-grow: 0;
  width: 5rem;
  height: 5rem;
  background-color: transparent;
  border: none;
  padding: 0;
  //margin: 0 1rem;
  color: $brand-green;

  &:before {
    position: absolute;
    content: "";
    height: 3px;
    width: 18px;
    border-radius: 1.5px;
    background-color: currentColor;
  }

  &.housemate-picker__btn--plus:after {
    position: absolute;
    content: "";
    height: 18px;
    width: 3px;
    border-radius: 1.5px;
    background-color: currentColor;
  }

  &:hover {
    color: $black;
    cursor: pointer;
  }
}

.housemate-picker__btn-text {
  @include visuallyhidden;
}

.housemate-picker__input {
  width: calc(100% - 100px);
  text-align: center;
  border: 0;
  margin-bottom: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}

input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.form__submit {
  margin-top: $spacing-medium;
}

.back-link {
  padding: 0 4rem;
}
</style>
